import type { OnInit } from '@angular/core';
import { Component, DestroyRef, ElementRef, HostListener, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { ControlValueAccessor } from '@angular/forms';
import { FormControl } from '@angular/forms';
import type { ConnectedPosition, ScrollStrategy } from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { makeProvider } from '@shared/form-fields/make-provider';

@Component({
    selector: 'app-suggestions-input',
    templateUrl: './suggestions-input.component.html',
    styleUrls: ['./suggestions-input.component.scss'],
    providers: [makeProvider(SuggestionsInputComponent)],
})
export class SuggestionsInputComponent implements ControlValueAccessor, OnInit {
    private static nextId = 0;

    @Input() label!: string;
    @Input() maxLength?: number;
    @Input() suggestions: readonly string[] = [];

    readonly inputControl = new FormControl('', { nonNullable: true });
    readonly listboxId = `cll-suggestions-listbox-${++SuggestionsInputComponent.nextId}`;

    filteredSuggestions: string[] = [];
    isOpen = false;
    activeIndex = -1;

    readonly positions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    ];

    // While the panel is open only the suggestions list scrolls, so the input can't be scrolled out of view.
    // block() locks the page; the nearest scrollable ancestor (e.g. cll-dialog-content) is locked separately.
    readonly scrollStrategy: ScrollStrategy = inject(Overlay).scrollStrategies.block();

    private readonly destroyRef = inject(DestroyRef);
    private hasFocus = false;
    private lockedAncestor: HTMLElement | null = null;
    private lockedAncestorOverflowY = '';

    constructor(private readonly hostRef: ElementRef<HTMLElement>) {}

    private onChange: (value: string) => void = () => {};
    protected onTouched: () => void = () => {};

    get panelWidth(): number {
        return this.hostRef.nativeElement.offsetWidth;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.hostRef.nativeElement.contains(event.target as Node)) {
            this.isOpen = false;
        }
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        if (!this.isOpen) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.activeIndex = Math.min(this.activeIndex + 1, this.filteredSuggestions.length - 1);
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.activeIndex = Math.max(this.activeIndex - 1, -1);
        }

        if (event.key === 'Enter' && this.activeIndex >= 0) {
            event.preventDefault();
            this.selectOption(this.filteredSuggestions[this.activeIndex]);
        }

        if (event.key === 'Escape') {
            this.isOpen = false;
        }
    }

    ngOnInit(): void {
        this.destroyRef.onDestroy(() => this.unlockAncestorScroll());

        this.inputControl.valueChanges.pipe(
            tap(value => this.onChange(value)),
            debounceTime(100),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(value => {
            this.filteredSuggestions = this._filter(value);
            this.isOpen = this.hasFocus;
            this.activeIndex = -1;
        });
    }

    onFocus(): void {
        this.hasFocus = true;
        this.activeIndex = -1;
        this.filteredSuggestions = this._filter(this.inputControl.value);
        this.isOpen = true;
    }

    onBlur(): void {
        this.hasFocus = false;
        this.isOpen = false;
        this.onTouched();
    }

    selectOption(option: string): void {
        this.inputControl.setValue(option, { emitEvent: false });
        this.onChange(option);
        this.isOpen = false;
        this.activeIndex = -1;
    }

    lockAncestorScroll(): void {
        const ancestor = this.findScrollableAncestor(this.hostRef.nativeElement);

        if (!ancestor) {
            return;
        }

        this.lockedAncestor = ancestor;
        this.lockedAncestorOverflowY = ancestor.style.overflowY;
        ancestor.style.overflowY = 'hidden';
    }

    unlockAncestorScroll(): void {
        if (!this.lockedAncestor) {
            return;
        }

        this.lockedAncestor.style.overflowY = this.lockedAncestorOverflowY;
        this.lockedAncestor = null;
    }

    private findScrollableAncestor(element: HTMLElement): HTMLElement | null {
        let node = element.parentElement;

        while (node) {
            const overflowY = getComputedStyle(node).overflowY;

            if (overflowY === 'auto' || overflowY === 'scroll') {
                return node;
            }

            node = node.parentElement;
        }

        return null;
    }

    private _filter(value: string): string[] {
        if (!value) {
            return this.suggestions.slice();
        }

        const lower = value.toLowerCase();
        const matches = this.suggestions.filter(s => s.toLowerCase().includes(lower));

        if (matches.length === 1 && matches[0].toLowerCase() === lower) {
            return [];
        }

        return matches;
    }

    writeValue(value: string): void {
        this.inputControl.setValue(value ?? '', { emitEvent: false });
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.inputControl.disable();
        } else {
            this.inputControl.enable();
        }
    }
}
